import { useState } from 'react'
import {
  Badge,
  Card,
  CardHeader,
  DataTable,
  ErrorState,
  FilterBar,
  LaporanTampil,
  PageHeader,
  FILTER_KOSONG,
  ambilBaris,
  type KolomTabel,
  type NilaiFilter,
} from '@/components'
import { formatRupiah, tanggalHariIni, awalTahunIni, formatTanggal } from '@/lib/format'
import { useApi } from '@/lib/useApi'
import { useOpsiUnit } from '@/lib/opsi'

interface BarisPengecualian {
  id?: string
  transferNumber?: string
  sourceUnit?: { name?: string }
  destinationUnit?: { name?: string }
  sourceUnitName?: string
  destinationUnitName?: string
  amount?: number
  status?: string
  varianceAmount?: number
  matchingReference?: string
  transferDate?: string
  [kunci: string]: unknown
}

export default function LaporanKonsolidasi() {
  const opsiUnit = useOpsiUnit()
  const [filter, setFilter] = useState<NilaiFilter>({
    ...FILTER_KOSONG,
    dari: awalTahunIni(),
    sampai: tanggalHariIni(),
  })

  const konsolidasi = useApi<unknown>('/reports/consolidation', {
    dari: filter.dari || undefined,
    sampai: filter.sampai || undefined,
    unitId: filter.unitId || undefined,
  })

  const pengecualian = useApi<unknown>('/transfer-exceptions', {})

  const baris = ambilBaris(pengecualian.data, ['items', 'rows', 'data', 'exceptions', 'daftar']) as BarisPengecualian[]

  const kolom: KolomTabel<BarisPengecualian>[] = [
    {
      kunci: 'transferNumber',
      judul: 'Nomor Transfer',
      render: (b) => <span className="font-medium text-netral-800">{String(b.transferNumber ?? '—')}</span>,
    },
    {
      kunci: 'sourceUnit',
      judul: 'Dari Unit',
      render: (b) => b.sourceUnit?.name ?? b.sourceUnitName ?? '—',
    },
    {
      kunci: 'destinationUnit',
      judul: 'Ke Unit',
      render: (b) => b.destinationUnit?.name ?? b.destinationUnitName ?? '—',
    },
    { kunci: 'transferDate', judul: 'Tanggal', render: (b) => (b.transferDate ? formatTanggal(String(b.transferDate)) : '—') },
    {
      kunci: 'amount',
      judul: 'Jumlah',
      rata: 'kanan',
      render: (b) => formatRupiah(Number(b.amount ?? 0)),
    },
    {
      kunci: 'varianceAmount',
      judul: 'Selisih',
      rata: 'kanan',
      render: (b) =>
        Number(b.varianceAmount ?? 0) !== 0 ? (
          <span className="font-medium text-red-700">{formatRupiah(Number(b.varianceAmount))}</span>
        ) : (
          '—'
        ),
    },
    { kunci: 'status', judul: 'Status', render: (b) => <Badge nada="peringatan">{String(b.status ?? 'BELUM COCOK')}</Badge> },
  ]

  return (
    <div>
      <PageHeader
        judul="Laporan Konsolidasi"
        deskripsi="Gabungan seluruh unit yang dikonsolidasi setelah eliminasi transfer internal. Transfer yang belum cocok DIKECUALIKAN dan ditampilkan terpisah."
        ekspor={{
          path: '/reports/consolidation',
          namaDasar: 'laporan-konsolidasi',
          query: { dari: filter.dari || undefined, sampai: filter.sampai || undefined, unitId: filter.unitId || undefined },
        }}
      />

      <FilterBar
        nilai={filter}
        onUbah={setFilter}
        unit={opsiUnit}
        tampilkan={{ unitId: true, dari: true, sampai: true, fundId: false, program: false, kategori: false }}
      />

      <Card className="mb-5 border-emas-300 bg-emas-100/40">
        <CardHeader
          judul="Catatan Eliminasi"
          deskripsi="Mutasi antarunit dieliminasi agar tidak terhitung ganda. Transfer yang belum dicocokkan (belum MATCHED) tidak dieliminasi dan menjadi pengecualian."
        />
      </Card>

      <div className="mb-6">
        <LaporanTampil
          data={konsolidasi.data}
          memuat={konsolidasi.memuat}
          galat={konsolidasi.galat}
          kodeGalat={konsolidasi.kodeGalat}
          onCoba={konsolidasi.muat}
          kosong={<p className="py-6 text-center text-sm text-netral-500">Data konsolidasi belum tersedia.</p>}
        />
      </div>

      <Card padat className="overflow-hidden">
        <div className="border-b border-netral-200 px-4 py-2.5">
          <h2 className="text-sm font-semibold text-netral-800">Pengecualian: Transfer Antarunit Belum Cocok</h2>
          <p className="mt-0.5 text-xs text-netral-500">
            Daftar ini berasal dari GET /transfer-exceptions.
          </p>
        </div>
        {pengecualian.galat ? (
          <div className="p-3">
            <ErrorState pesan={pengecualian.galat} kode={pengecualian.kodeGalat} onCoba={pengecualian.muat} ringkas />
          </div>
        ) : (
          <DataTable
            kolom={kolom}
            data={baris}
            kunciBaris={(b, i) => String(b.id ?? b.transferNumber ?? `kec-${i}`)}
            memuat={pengecualian.memuat}
            pesanKosong="Tidak ada transfer antarunit yang belum cocok."
          />
        )}
      </Card>
    </div>
  )
}
